// src/pages/teacher/PhonicsWorksheetGenerator.tsx
// UPDATED: Now uses real mood-based worksheet generation
import React, { useState, useEffect } from 'react'
import { analytics } from '../../services/analytics-firebase'
import { ArrowLeft, FileText, Download, Loader2, Eye, Brain, ChevronDown } from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { generateMoodBasedWorksheet } from '../../services/phonicsWorksheetService'
import { generateMoodBasedPDF } from '../../services/moodBasedPDFGenerator'
import { useUserTier, useUsageLimit, useTrackGeneration } from '../../hooks/useUsageTracking'
import { WeeklyUsageCounter } from '../../components/worksheets/WeeklyUsageCounter'
import { UpgradeModal } from '../../components/worksheets/UpgradeModal'
import type { WorksheetData, PhonicsPattern, MoodType } from '../../types/phonicsWorksheet'

const PATTERNS = [
  { id: 'cvc' as const, name: 'CVC Words', examples: 'cat, dog, sun', desc: 'Simple three-letter words' },
  { id: 'magic_e' as const, name: 'Magic E', examples: 'cake, bike, home', desc: 'Silent e changes the vowel' },
  { id: 'blends' as const, name: 'Blends', examples: 'stop, frog, clap', desc: 'Two consonants together' },
  { id: 'digraphs' as const, name: 'Digraphs', examples: 'ship, chat, when', desc: 'Two letters, one sound' },
  { id: 'vowel_teams' as const, name: 'Vowel Teams', examples: 'rain, boat, tree', desc: 'Vowels working together' }
]

const MOODS = [
  {
    id: 'overwhelmed' as const,
    emoji: '🌊',
    name: 'OVERWHELMED',
    description: '"My child is struggling today"',
    features: [
      '4 simple words',
      'Tons of white space',
      'Calm colors',
      '2 gentle activities',
      '"Do what feels right"'
    ],
    colors: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      borderSelected: 'border-blue-600',
      bgSelected: 'bg-blue-100',
      ring: 'ring-blue-200',
      text: 'text-blue-900',
      textBody: 'text-blue-800'
    }
  },
  {
    id: 'high_energy' as const,
    emoji: '⚡',
    name: 'HIGH ENERGY',
    description: '"My child is ready to work hard"',
    features: [
      '10 varied words',
      'More practice items',
      'Bright, energizing colors',
      '3 active tasks',
      'Movement breaks'
    ],
    colors: {
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      borderSelected: 'border-orange-600',
      bgSelected: 'bg-orange-100',
      ring: 'ring-orange-200',
      text: 'text-orange-900',
      textBody: 'text-orange-800'
    }
  },
  {
    id: 'steady' as const,
    emoji: '🌱',
    name: 'STEADY',
    description: '"My child is in a good headspace"',
    features: [
      '6-7 balanced words',
      'Standard difficulty',
      'Friendly blue tones',
      '2-3 activities',
      'Typical worksheet format'
    ],
    colors: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      borderSelected: 'border-purple-600',
      bgSelected: 'bg-purple-100',
      ring: 'ring-purple-200',
      text: 'text-purple-900',
      textBody: 'text-purple-800'
    }
  }
]

export const PhonicsWorksheetGenerator: React.FC = () => {
  // State for the three-step process
  const [selectedPattern, setSelectedPattern] = useState<PhonicsPattern | ''>('')
  const [selectedMood, setSelectedMood] = useState<MoodType | ''>('')
  const [skipOptions, setSkipOptions] = useState({
    noWriting: false,
    noRhyming: false,
    visualOnly: false
  })
  const [showSkipOptions, setShowSkipOptions] = useState(false)
  
  // Worksheet generation state
  const [isLoading, setIsLoading] = useState(false)
  const [worksheetData, setWorksheetData] = useState<WorksheetData | null>(null)

  // Usage tracking
  const { tier, isPremium } = useUserTier()
  const usageLimit = useUsageLimit()
  const { remaining, canGenerate, loading: usageLoading, resetDate, daysUntilReset, refetch, recheckCredits } = usageLimit
  const { trackGeneration } = useTrackGeneration()
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)

  const [complexityOverride, setComplexityOverride] = useState<'auto' | 'easy' | 'regular' | 'challenge'>('auto')
  const [showAgeModal, setShowAgeModal] = useState(false)

  useEffect(() => {
    const hasAge = localStorage.getItem('worksheet-age-range')
    if (!hasAge) {
      setShowAgeModal(true)
    }
  }, [])

  // Track page view
  useEffect(() => {
    analytics.pageView('worksheet_generator_phonics', {
      user_type: 'teacher',
      is_premium: isPremium
    })
  }, [isPremium])

  const canGenerateWorksheet = selectedPattern !== '' && selectedMood !== ''

  const generateWorksheet = async () => {
    if (!selectedMood || !selectedPattern) return
    
    // Check LIVE credits from Firestore
    if (!isPremium) {
      const currentCredits = await recheckCredits()
      const currentUsed = usageLimit.used || 0
      const freshCanGenerate = (currentUsed < 3) || currentCredits > 0
      
      if (!freshCanGenerate) {
        setShowUpgradeModal(true)
        analytics.upgradeModalShown('worksheet_limit_reached')
        return
      }
    }
  
    setIsLoading(true)
    try {
      // Track generation FIRST to prevent exploits
      if (!isPremium) {
        try {
          await trackGeneration()
        } catch (error) {
          setIsLoading(false)
          alert('Failed to track usage. Please try again.')
          return
        }
      }
      
      // Generate the worksheet AFTER tracking
      const result = await generateMoodBasedWorksheet({
        pattern: selectedPattern as PhonicsPattern,
        mood: selectedMood as MoodType,
        skipOptions,
        complexityOverride: complexityOverride !== 'auto' ? complexityOverride : undefined,  
        ageRange: localStorage.getItem('worksheet-age-range') || undefined
      })
  
      setWorksheetData(result)
      // Track generation
      analytics.worksheetGenerated('phonics', selectedPattern + '_' + selectedMood, isPremium)
      
      // Refetch to update counter
      if (!isPremium) {
        await refetch()
      }
    } catch (error) {
      console.error('Error generating worksheet:', error)
      alert('Failed to generate worksheet. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownloadPDF = async () => {
    if (!worksheetData) return
    
    try {
      const filename = `${worksheetData.config.pattern}_${worksheetData.config.mood}_${Date.now()}.pdf`
      await generateMoodBasedPDF(worksheetData, filename)
      // Track download
      analytics.worksheetDownloaded('phonics', selectedPattern + '_' + selectedMood, isPremium)
    } catch (error) {
      console.error('Error downloading PDF:', error)
      alert('Failed to download PDF. Please try again.')
    }
  }

  const handleGenerateAnother = () => {
    setShowPreview(false);
    setGeneratedWorksheet(null);
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => window.location.href = '/skill-builder'} 
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Phonics Worksheet Generator</h1>
              <p className="text-sm text-gray-600">Match worksheets to your child's current state</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        
        {/* Pattern Selection - NOW FIRST */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">What pattern are you working on?</h2>

          <div className="flex flex-wrap gap-4">
            {PATTERNS.map((pattern) => (
              <button
                key={pattern.id}
                onClick={() => setSelectedPattern(pattern.id)}
                className={`w-44 md:w-52 p-4 rounded-2xl border transition-all flex flex-col items-center text-center 
                  ${
                    selectedPattern === pattern.id
                      ? 'border-blue-300 bg-blue-100/60 text-blue-900 shadow-sm scale-[1.02]'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-blue-50 hover:border-blue-200 hover:shadow-sm'
                  }
                `}
              >
                <div className="font-semibold text-base mb-1">
                  {pattern.name}
                </div>

                <div className="text-sm text-gray-500">
                  ({pattern.examples.split(',').slice(0, 3).join(', ')}
                  {pattern.examples.split(',').length > 3 ? '…' : ''})
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Mood Selection - NOW SECOND */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">How's your child today?</h3>
          <div className="grid grid-cols-3 gap-3">
            {MOODS.map((mood) => (
              <button
                key={mood.id}
                onClick={() => setSelectedMood(mood.id)}
                className={`p-4 rounded-lg border-2 transition-all ${
                  selectedMood === mood.id
                    ? `${mood.colors.borderSelected} ${mood.colors.bgSelected}`
                    : `${mood.colors.border} ${mood.colors.bg} hover:${mood.colors.bgSelected}`
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="text-4xl">{mood.emoji}</div>
                  <div className="flex-1 text-left">
                    <div className="font-semibold text-base">{mood.name}</div>
                    <div className="text-xs text-gray-600 mt-1">{mood.description}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Word Complexity Override - BETWEEN PATTERN AND SKIP OPTIONS */}
        {selectedPattern && (
          <div className="mb-8">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Word Challenge Level</h3>
                  <p className="text-xs text-gray-600 mt-1">
                    {complexityOverride === 'auto' 
                      ? '⚙️ What difficulty level do you want to go for today?' 
                      : `Using ${complexityOverride} difficulty words`}
                  </p>
                </div>
                <button
                  onClick={() => setComplexityOverride(complexityOverride === 'auto' ? 'regular' : 'auto')}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  {complexityOverride === 'auto' ? 'Customize' : 'Use Auto'}
                </button>
              </div>
              
              {complexityOverride !== 'auto' && (
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setComplexityOverride('easy')}
                    className={`text-xs p-2 rounded border flex flex-col items-center text-center ${
                      complexityOverride === 'easy'
                        ? 'bg-green-100 border-green-600 text-green-900 font-medium'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    <span className="text-sm">Easy</span>
                    <span className="text-[14px] text-gray-700 mt-0.5">🌱 Confidence Builder</span>
                  </button>
                  <button
                    onClick={() => setComplexityOverride('regular')}
                    className={`text-xs p-2 rounded border flex flex-col items-center text-center ${
                      complexityOverride === 'regular'
                        ? 'bg-blue-100 border-blue-600 text-blue-900 font-medium'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    <span className="text-sm">Regular</span>
                    <span className="text-[14px] text-gray-700 mt-0.5">🎯 Steady Practice</span>
                  </button>
                  <button
                    onClick={() => setComplexityOverride('challenge')}
                    className={`text-xs p-2 rounded border flex flex-col items-center text-center ${
                      complexityOverride === 'challenge'
                        ? 'bg-purple-100 border-purple-600 text-purple-900 font-medium'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    <span className="text-sm">Challenge</span>
                    <span className="text-[14px] text-gray-700 mt-0.5">🚀 Ready to Stretch</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Generate Button - THIRD */}
        <div className="flex justify-center mb-8">
          {!worksheetData ? (
            <Button
              onClick={generateWorksheet}
              disabled={!selectedMood || !selectedPattern || isLoading}
              size="lg"
              className="min-w-[200px]"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating...
                </>
              ) : (
                'Generate Worksheet'
              )}
            </Button>
          ) : (
            <Button
              onClick={handleDownloadPDF}
              size="lg"
              className="min-w-[200px]"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          )}
        </div>

        {/* Usage Counter - FOURTH */}
        {!isPremium && (
          <div className="mb-8">
            <WeeklyUsageCounter
              remaining={remaining}
              resetDate={resetDate}
              onUpgrade={() => setShowUpgradeModal(true)}
            />
          </div>
        )}

        {/* Worksheet Preview - FIFTH */}
        {worksheetData && (
          <div className="bg-white p-6 rounded-lg border border-green-200 shadow-sm">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-green-800 mb-2">✓ Worksheet Ready</h3>
              <p className="text-gray-600 mb-1">
                {worksheetData.wordCount} words • {worksheetData.estimatedTime} minutes
              </p>
              <p className="text-sm text-gray-500">
                Words: {worksheetData.words.join(', ')}
              </p>
            </div>

            <div className="space-y-3">
              <Button 
                onClick={handleDownloadPDF} 
                className="w-full"
                size="lg"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>

              {isPremium ? (
                <Button
                  onClick={handleGenerateAnother}
                  variant="outline"
                  className="w-full"
                  size="lg"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Generating...
                    </>
                  ) : (
                    'Generate Another'
                  )}
                </Button>
              ) : (
                <Button
                  disabled
                  variant="outline"
                  className="w-full opacity-50 cursor-not-allowed"
                  size="lg"
                >
                  Generate Another
                  <span className="ml-2 px-2 py-0.5 bg-yellow-400 text-yellow-900 text-xs rounded font-bold">
                    PREMIUM
                  </span>
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Age Selection Modal - One-Time */}
      {showAgeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Quick Question</h2>
            <p className="text-sm text-gray-600 mb-6">
              To match words to your child's level, what's their age range?
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => {
                  localStorage.setItem('worksheet-age-range', 'young')
                  setShowAgeModal(false)
                }}
                className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50"
              >
                <div className="font-medium text-gray-900">Ages 6-8</div>
                <div className="text-xs text-gray-600">Early readers</div>
              </button>
              
              <button
                onClick={() => {
                  localStorage.setItem('worksheet-age-range', 'tween')
                  setShowAgeModal(false)
                }}
                className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50"
              >
                <div className="font-medium text-gray-900">Ages 9-12</div>
                <div className="text-xs text-gray-600">Older struggling readers</div>
              </button>
              
              <button
                onClick={() => {
                  localStorage.setItem('worksheet-age-range', 'teen')
                  setShowAgeModal(false)
                }}
                className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50"
              >
                <div className="font-medium text-gray-900">Ages 13+</div>
                <div className="text-xs text-gray-600">Teens needing support</div>
              </button>
            </div>
            
            <p className="text-xs text-gray-500 mt-4 text-center">
              You can change this anytime in settings
            </p>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          daysUntilReset={daysUntilReset}
        />
      )}
    </div>
  )
}